import { PaymentIntentMachine, type PaymentIntentStatus } from '@paguz/contracts';
import {
  Badge,
  Button,
  EmptyState,
  formatCop,
  MoneyText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@paguz/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  FilterXIcon,
  SearchXIcon,
} from 'lucide-react';
import { useState } from 'react';
import type { AdminPaymentIntentSummary } from '../../api/admin-schemas.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { Field, Select, compactInputClass } from '../../components/form-controls.tsx';
import { ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { formatDateTime, shortId } from '../../lib/format.ts';
import { CHANNEL_LABEL, FUNDING_MODE, PAYMENT_STATUS } from '../../lib/labels.ts';
import { cn, inputBaseClass } from '@paguz/ui';
import type { TransactionsSearch } from '../../app/search.ts';

const route = getRouteApi('/app/transacciones');

export function TransactionsPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const query = useQuery({
    queryKey: queryKeys.paymentIntents(search),
    queryFn: () => api.listPaymentIntents(search),
    placeholderData: keepPreviousData,
  });

  const setFilters = (next: TransactionsSearch) => {
    setCursorStack([]);
    void navigate({ search: { ...next, cursor: undefined } });
  };

  const hasFilters = Object.entries(search).some(([key, value]) => key !== 'cursor' && value);

  return (
    <>
      <PageHeader
        title="Transacciones"
        description="Compras con cupo pagadas por Bre-B. Abre una para ver el timeline de la saga, las llamadas externas y las acciones de operador."
        actions={
          <Button
            variant="outline"
            disabled={!query.data?.data.length}
            onClick={() => {
              if (query.data) downloadCsv(query.data.data);
            }}
          >
            <DownloadIcon /> Exportar CSV
          </Button>
        }
      />
      <Filters key={JSON.stringify(search)} initial={search} onApply={setFilters} />

      <Panel className="mt-5" bodyClassName="p-0">
        {query.isPending ? (
          <div className="p-5">
            <LoadingRows />
          </div>
        ) : query.isError ? (
          <div className="p-5">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        ) : query.data.data.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={<SearchXIcon />}
              title="No hay transacciones con esos filtros"
              description="Ajusta la búsqueda o limpia los filtros."
              action={
                hasFilters && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFilters({});
                    }}
                  >
                    <FilterXIcon /> Limpiar filtros
                  </Button>
                )
              }
            />
          </div>
        ) : (
          <>
            <Table className={cn(query.isPlaceholderData && 'opacity-60')}>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Comercio</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead className="text-center">Cuotas</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead>ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatDateTime(row.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/transacciones/$paymentIntentId"
                        params={{ paymentIntentId: row.id }}
                        className="font-semibold text-primary hover:underline"
                      >
                        {row.payeeName}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {CHANNEL_LABEL[row.channel]} · {FUNDING_MODE[row.fundingMode].label}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{row.customer.fullName}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.customer.maskedDocument}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyText value={row.amount.amount} size="sm" className="font-semibold" />
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{row.installments}</TableCell>
                    <TableCell>
                      <Badge variant={PAYMENT_STATUS[row.status].tone}>
                        {PAYMENT_STATUS[row.status].label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {row.reasonCode ? (
                        <code className="font-mono text-xs">{row.reasonCode}</code>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-xs text-muted-foreground" title={row.id}>
                        {shortId(row.id)}
                      </code>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between border-t border-border px-5 py-3 text-sm text-muted-foreground">
              <span>
                {query.data.data.length} resultados en esta página
                {query.isFetching && ' · actualizando…'}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={cursorStack.length === 0}
                  onClick={() => {
                    const prev = cursorStack.slice(0, -1);
                    setCursorStack(prev);
                    void navigate({ search: { ...search, cursor: prev.at(-1) } });
                  }}
                >
                  <ChevronLeftIcon /> Anterior
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!query.data.nextCursor}
                  onClick={() => {
                    const next = query.data.nextCursor ?? undefined;
                    if (!next) return;
                    setCursorStack([...cursorStack, next]);
                    void navigate({ search: { ...search, cursor: next } });
                  }}
                >
                  Siguiente <ChevronRightIcon />
                </Button>
              </div>
            </div>
          </>
        )}
      </Panel>
    </>
  );
}

const STATUSES = PaymentIntentMachine.states as readonly PaymentIntentStatus[];

function Filters({
  initial,
  onApply,
}: {
  initial: TransactionsSearch;
  onApply: (next: TransactionsSearch) => void;
}) {
  const [draft, setDraft] = useState<TransactionsSearch>(initial);
  const set = (key: keyof TransactionsSearch, value: string) => {
    setDraft((d) => ({ ...d, [key]: value === '' ? undefined : value }));
  };
  const input = cn(inputBaseClass, compactInputClass);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onApply(draft);
      }}
      className="grid gap-3 rounded-2xl border border-border bg-card p-4 md:grid-cols-6 xl:grid-cols-[2fr_1.2fr_1fr_1fr_1fr_1fr_1fr_auto]"
      aria-label="Filtros de transacciones"
    >
      <Field label="Buscar" htmlFor="f-q" className="md:col-span-3 xl:col-span-1">
        <input
          id="f-q"
          className={input}
          placeholder="pi_…, endToEndId, cliente o comercio"
          value={draft.q ?? ''}
          onChange={(e) => {
            set('q', e.target.value);
          }}
        />
      </Field>
      <Field label="Estado" htmlFor="f-status">
        <Select
          id="f-status"
          value={draft.status ?? ''}
          onChange={(e) => {
            set('status', e.target.value);
          }}
        >
          <option value="">Todos</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {PAYMENT_STATUS[s].label}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Código PZ" htmlFor="f-code">
        <input
          id="f-code"
          className={cn(input, 'font-mono')}
          placeholder="PZ-BRB"
          value={draft.reasonCode ?? ''}
          onChange={(e) => {
            set('reasonCode', e.target.value);
          }}
        />
      </Field>
      <Field label="Desde" htmlFor="f-from">
        <input
          id="f-from"
          type="date"
          className={input}
          value={draft.from ?? ''}
          onChange={(e) => {
            set('from', e.target.value);
          }}
        />
      </Field>
      <Field label="Hasta" htmlFor="f-to">
        <input
          id="f-to"
          type="date"
          className={input}
          value={draft.to ?? ''}
          onChange={(e) => {
            set('to', e.target.value);
          }}
        />
      </Field>
      <Field label="Monto mín." htmlFor="f-min">
        <input
          id="f-min"
          inputMode="numeric"
          className={input}
          placeholder="$"
          value={draft.minAmount ?? ''}
          onChange={(e) => {
            set('minAmount', e.target.value.replace(/\D/g, ''));
          }}
        />
      </Field>
      <Field label="Monto máx." htmlFor="f-max">
        <input
          id="f-max"
          inputMode="numeric"
          className={input}
          placeholder="$"
          value={draft.maxAmount ?? ''}
          onChange={(e) => {
            set('maxAmount', e.target.value.replace(/\D/g, ''));
          }}
        />
      </Field>
      <div className="flex items-end gap-2 md:col-span-6 xl:col-span-1">
        <Button type="submit" variant="highlight" className="h-10">
          Filtrar
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-10"
          onClick={() => {
            setDraft({});
            onApply({});
          }}
        >
          Limpiar
        </Button>
      </div>
    </form>
  );
}

const BOM = String.fromCharCode(0xfeff);

/** CSV de la página actual con los datos tal como los entrega la API (ya enmascarados por rol). */
function downloadCsv(rows: readonly AdminPaymentIntentSummary[]) {
  const header = [
    'fecha',
    'id',
    'estado',
    'cliente',
    'documento',
    'comercio',
    'monto',
    'cuotas',
    'codigo',
    'endToEndId',
  ];
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const lines = rows.map((r) =>
    [
      r.createdAt,
      r.id,
      r.status,
      r.customer.fullName,
      r.customer.maskedDocument,
      r.payeeName,
      formatCop(r.amount.amount),
      String(r.installments),
      r.reasonCode ?? '',
      r.endToEndId ?? '',
    ]
      .map(escape)
      .join(','),
  );
  const blob = new Blob([`${BOM}${[header.join(','), ...lines].join('\n')}`], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'transacciones.csv';
  a.click();
  URL.revokeObjectURL(url);
}
